import { GEMATIK_ROOM, MAX_TOPIC_LENGTH } from '../constants';
import logger from './logger';

/**
 * Get room display name with Gematik-specific event support.
 *
 * Checks for custom Gematik room name state event (de.gematik.tim.room.name)
 * before falling back to standard Matrix room name (m.room.name).
 *
 * This is required for Gematik TI-Messenger compliance where custom state
 * events carry authoritative room metadata.
 *
 * @param {Room} room - Matrix Room object from matrix-js-sdk
 * @param {string} [fallback='Unnamed Room'] - Default value if no name found
 * @returns {string} Room display name or fallback
 *
 * @example
 * const displayName = getRoomDisplayName(room);
 * // Returns: "Team Discussion" (from Gematik event if present)
 * //       or "Team Discussion" (from Matrix event if Gematik not present)
 * //       or "Unnamed Room" (if neither present)
 */
export const getRoomDisplayName = (room, fallback = 'Unnamed Room') => {
  // Defensive check for room initialization phase
  if (!room?.currentState) {
    return fallback;
  }

  // First check for Gematik custom name event
  const gematikNameEvent = room.currentState.getStateEvents(GEMATIK_ROOM.ROOM_NAME, '');
  if (gematikNameEvent) {
    const content = gematikNameEvent.getContent();
    const name = content?.name;
    // Explicit type check and non-empty validation
    if (typeof name === 'string' && name.trim()) {
      return name;
    }
  }

  // Fallback to standard Matrix room name
  return room.name || fallback;
};

/**
 * Get room topic with Gematik-specific event support.
 *
 * Checks for custom Gematik room topic state event (de.gematik.tim.room.topic)
 * before falling back to standard Matrix room topic (m.room.topic).
 *
 * This is required for Gematik TI-Messenger compliance where custom state
 * events carry authoritative room metadata.
 *
 * @param {Room} room - Matrix Room object from matrix-js-sdk
 * @param {string} [fallback=''] - Default value if no topic found
 * @returns {string} Room topic or fallback
 *
 * @example
 * const topic = getRoomTopic(room);
 * // Returns: "Project updates and discussions" (from Gematik event if present)
 * //       or "Project updates" (from Matrix event if Gematik not present)
 * //       or "" (if neither present)
 */
export const getRoomTopic = (room, fallback = '') => {
  // Defensive check for room initialization phase
  if (!room?.currentState) {
    return fallback;
  }

  // First check for Gematik custom topic event
  const gematikTopicEvent = room.currentState.getStateEvents(GEMATIK_ROOM.ROOM_TOPIC, '');
  if (gematikTopicEvent) {
    const topic = gematikTopicEvent.getContent()?.topic;
    // Explicit type check and non-empty validation (consistent with getRoomDisplayName)
    if (typeof topic === 'string' && topic.trim()) {
      return topic;
    }
  }

  // Fallback to standard Matrix room topic
  const standardTopicEvent = room.currentState.getStateEvents('m.room.topic', '');
  if (standardTopicEvent) {
    const topic = standardTopicEvent.getContent()?.topic;
    if (typeof topic === 'string' && topic.trim()) {
      return topic;
    }
  }

  return fallback;
};

/**
 * Check if a user has permission to edit the room name.
 *
 * Checks the power level required for `m.room.name` state events,
 * falling back to `state_default`, then `50` (Matrix spec default).
 *
 * @param {Object} powerLevels - Power levels content from m.room.power_levels
 * @param {number} userPowerLevel - The user's current power level
 * @returns {boolean} Whether the user can edit the room name
 */
export const canUserEditRoomName = (powerLevels, userPowerLevel) => {
  if (!powerLevels) return false;
  const nameLevel = powerLevels.events?.['m.room.name']
    ?? powerLevels.state_default
    ?? 50;
  return userPowerLevel >= nameLevel;
};

/**
 * Check if a user has permission to edit the room topic.
 *
 * Checks `m.room.topic`, then the Gematik custom topic event type,
 * then `state_default`, then `50` (Matrix spec default).
 *
 * @param {Object} powerLevels - Power levels content from m.room.power_levels
 * @param {number} userPowerLevel - The user's current power level
 * @returns {boolean} Whether the user can edit the room topic
 */
export const canUserEditRoomTopic = (powerLevels, userPowerLevel) => {
  if (!powerLevels) return false;
  const topicLevel = powerLevels.events?.['m.room.topic']
    ?? powerLevels.events?.[GEMATIK_ROOM.ROOM_TOPIC]
    ?? powerLevels.state_default
    ?? 50;
  return userPowerLevel >= topicLevel;
};

/**
 * Check whether a room has an explicitly set name via state events.
 *
 * Returns true if either the Gematik custom name event or the standard
 * m.room.name event contains a non-empty name string. This indicates a
 * user deliberately named the room (as opposed to the SDK-generated
 * display name derived from member names).
 *
 * @param {Room} room - Matrix Room object from matrix-js-sdk
 * @returns {boolean} True if the room has an explicit name set
 */
export const hasExplicitRoomName = (room) => {
  if (!room?.currentState) return false;

  const gematikNameEvent = room.currentState.getStateEvents(GEMATIK_ROOM.ROOM_NAME, '');
  const standardNameEvent = room.currentState.getStateEvents('m.room.name', '');

  let result = false;
  if (gematikNameEvent) {
    const name = gematikNameEvent.getContent()?.name;
    if (typeof name === 'string' && name.trim()) result = true;
  }

  if (!result && standardNameEvent) {
    const name = standardNameEvent.getContent()?.name;
    if (typeof name === 'string' && name.trim()) result = true;
  }

  return result;
};

/**
 * Dual-write room name to both Gematik and standard Matrix state events.
 *
 * Skips the write if the new name is empty or unchanged from the current name.
 * See: gemSpec_TI-M_Basis_V1.1.2 section 5.5.1.1
 *
 * @param {MatrixClient} client - The Matrix client instance
 * @param {string} roomId - The room ID to update
 * @param {string} newName - The new room name (will be trimmed)
 * @param {string} currentName - The current room name for change detection
 * @returns {Promise<boolean>} True if a write was performed, false if skipped
 */
export const dualWriteRoomName = async (client, roomId, newName, currentName) => {
  const trimmed = newName?.trim() || '';
  const current = currentName?.trim() || '';
  if (!trimmed || trimmed === current) {
    return false;
  }
  await client.sendStateEvent(roomId, GEMATIK_ROOM.ROOM_NAME, { name: trimmed }, '');
  await client.sendStateEvent(roomId, 'm.room.name', { name: trimmed }, '');
  logger.info('Dual-wrote room name:', trimmed);
  return true;
};

/**
 * Dual-write room topic to both Gematik and standard Matrix state events.
 *
 * Validates against MAX_TOPIC_LENGTH. Skips the write if unchanged.
 * See: gemSpec_TI-M_Basis_V1.1.2 section 5.5.1.1
 *
 * @param {MatrixClient} client - The Matrix client instance
 * @param {string} roomId - The room ID to update
 * @param {string} newTopic - The new room topic (will be trimmed)
 * @param {string} currentTopic - The current room topic for change detection
 * @returns {Promise<boolean>} True if a write was performed, false if skipped
 * @throws {Error} If the topic exceeds MAX_TOPIC_LENGTH
 */
export const dualWriteRoomTopic = async (client, roomId, newTopic, currentTopic) => {
  const trimmed = newTopic?.trim() || '';
  const current = currentTopic?.trim() || '';
  if (trimmed.length > MAX_TOPIC_LENGTH) {
    throw new Error(`Topic must be ${MAX_TOPIC_LENGTH} characters or less`);
  }
  if (trimmed === current) {
    return false;
  }
  await client.sendStateEvent(roomId, GEMATIK_ROOM.ROOM_TOPIC, { topic: trimmed }, '');
  await client.sendStateEvent(roomId, 'm.room.topic', { topic: trimmed }, '');
  logger.info('Dual-wrote room topic:', trimmed);
  return true;
};